/**
 * Draw Polygon Control for Mapbox GL.
 *
 * Requires: yarn add @mapbox/mapbox-gl-draw @types/mapbox__mapbox-gl-draw
 *
 * This component uses react-map-gl's `useControl` to mount the Mapbox GL Draw plugin,
 * allowing users to draw an AOI polygon on the map for spatial filtering.
 *
 * Supports two modes:
 * - Fresh draw (no initialPolygon) — user draws a new polygon, locks on completion
 * - Edit (initialPolygon provided) — pre-loads polygon in direct_select for vertex editing
 */
import { useControl, type IControl, type MapRef } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';

import {
  aoiPolygonAtom,
  aoiPolygonLockedAtom,
  broadcastSearchUpdateAtom,
} from '../atoms';

const drawStyles = [
  // Polygon fill — transparent
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': '#ff0000', 'fill-opacity': 0 },
  },
  // Polygon outline — thick red
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'line-color': '#ff0000', 'line-width': 3 },
  },
  // Vertex points
  {
    id: 'gl-draw-point',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
    paint: { 'circle-radius': 5, 'circle-color': '#ff0000' },
  },
  // Midpoints
  {
    id: 'gl-draw-point-mid',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': '#ff0000' },
  },
  // Line while drawing
  {
    id: 'gl-draw-line',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    paint: { 'line-color': '#ff0000', 'line-width': 3 },
  },
];

interface Props {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** When provided, pre-loads this polygon for editing instead of starting fresh draw. */
  initialPolygon?: GeoJSON.Polygon | null;
}

export const DrawPolygonControl = ({
  position = 'top-right',
  initialPolygon,
}: Props) => {
  const setAoiPolygon = useSetAtom(aoiPolygonAtom);
  const setLocked = useSetAtom(aoiPolygonLockedAtom);
  const broadcastUpdate = useSetAtom(broadcastSearchUpdateAtom);
  const drawRef = useRef<MapboxDraw | null>(null);

  const applyPolygon = useCallback(
    (e: { features: GeoJSON.Feature[] }) => {
      if (e.features.length > 0) {
        const polygon = e.features[0].geometry as GeoJSON.Polygon;
        setAoiPolygon(polygon);
        broadcastUpdate((current) => ({
          ...current,
          page: 1,
          bounding_box: undefined,
          polygon_geojson: polygon,
        }));
      }
    },
    [setAoiPolygon, broadcastUpdate]
  );

  const onCreate = useCallback(
    (e: { features: GeoJSON.Feature[] }) => {
      applyPolygon(e);
      setLocked(true);
    },
    [applyPolygon, setLocked]
  );

  const onDelete = useCallback(() => {
    setAoiPolygon(null);
    setLocked(true);
    broadcastUpdate((current) => ({
      ...current,
      page: 1,
      polygon_geojson: undefined,
    }));
  }, [setAoiPolygon, setLocked, broadcastUpdate]);

  useControl<IControl>(
    () => {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: !initialPolygon,
          trash: true,
        },
        defaultMode: initialPolygon ? 'simple_select' : 'draw_polygon',
        styles: drawStyles,
      });
      drawRef.current = draw;
      return draw as unknown as IControl;
    },
    ({ map }: { map: MapRef }) => {
      map.on('draw.create' as string, onCreate);
      map.on('draw.update' as string, applyPolygon);
      map.on('draw.delete' as string, onDelete);

      // Pre-load polygon for editing
      if (initialPolygon && drawRef.current) {
        const ids = drawRef.current.add({
          type: 'Feature',
          geometry: initialPolygon,
          properties: {},
        });
        if (ids.length > 0) {
          drawRef.current.changeMode('direct_select', {
            featureId: ids[0],
          });
        }
      }
    },
    ({ map }: { map: MapRef }) => {
      map.off('draw.create' as string, onCreate);
      map.off('draw.update' as string, applyPolygon);
      map.off('draw.delete' as string, onDelete);
    },
    { position }
  );

  return null;
};
